'use client'

import { useDevice } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import AccreditedIcon from '@public/icons/icon-accredited.png'
import GranularIcon from '@public/icons/icon-granular.png'
import BiofuelIcon from '@public/icons/lune-icon-biofuel.svg'
import HubIcon from '@public/icons/lune-icon-hub.svg'
import LiveChartIcon from '@public/icons/lune-icon-live-chart.svg'
import RouteIcon from '@public/icons/lune-icon-route.svg'
import AuditReadyCalculationBreakdowns from '@public/images/logistics/audit-ready-calculation-breakdowns.png'
import OutOfTheBoxReporting from '@public/images/logistics/out-of-the-box-reporting.png'
import InsettingCertificate from '@public/images/product/lune-carbon-insetting-section.png'
import QuoteOptions from '@public/images/solutions/lune-quote-options.png'
import TenderEmissions from '@public/images/solutions/lune-tender-emissions.png'

import AccordionTile from '@/components/AccordionTile'
import DownloadGuide from '@/components/DownloadGuide'
import GenericHero from '@/components/Hero/GenericHero'
import LearnMoreTiles from '@/components/LearnMoreTiles'
import PlugInAndGo from '@/components/PlugInAndGo'
import RequestDemo from '@/components/RequestDemo'
import RequestsShowcase, { type RequestsShowcaseItem } from '@/components/RequestsShowcase'
import LogisticsLogos from '@/sections/LogisticsLogos'
import LogixboardCaseStudyBox from '@/sections/LogixboardCaseStudyBox'
import { HUBSPOT_FORM_IDS } from '@/utills/constants'

const requests: RequestsShowcaseItem[] = [
    {
        request: '“Can you send the CO₂ for all our Q3 shipments, split by mode and lane?”',
        image: {
            src: OutOfTheBoxReporting,
            alt: 'Customer analytics page with data quality scores and emissions by transport mode.',
        },
        product: {
            name: 'Analytics & reporting',
            icon: LiveChartIcon,
            href: '/analytics-reporting',
        },
        description:
            'An analytics page for every customer, generated automatically, with emissions by mode, lane and period.',
    },
    {
        request: '“The tender asks for emissions per lane.”',
        image: {
            src: TenderEmissions,
            alt: 'Tender spreadsheet returned with estimated tCO₂e per lane added by Lune.',
        },
        product: {
            name: 'Emissions calculations',
            icon: RouteIcon,
            href: '/emissions-calculations',
        },
        description:
            "Upload the tender lanes and get estimates back, including sailings that haven't happened yet.",
    },
    {
        request: '“Our auditor wants to see how these figures were calculated.”',
        image: {
            src: AuditReadyCalculationBreakdowns,
            alt: 'Step-by-step calculation explanation with a data quality score.',
        },
        product: {
            name: 'Emissions calculations',
            icon: RouteIcon,
            href: '/emissions-calculations',
        },
        description:
            'Every figure comes with a step-by-step explanation, its emission factor and source, and a data quality score.',
    },
    {
        request: '“Which option on this lane has the lowest emissions?”',
        image: {
            src: QuoteOptions,
            alt: "A logistics provider's quote for Shanghai to Duisburg with five options, their price and CO₂, and the lowest-CO₂ option highlighted.",
        },
        product: {
            name: 'Emissions calculations',
            icon: RouteIcon,
            href: '/emissions-calculations',
        },
        description:
            'Calculate every option before booking, highlight the greenest, and show your customer what switching saves.',
    },
    {
        request: '“Can we cut the emissions of the freight we already ship?”',
        image: {
            src: InsettingCertificate,
            alt: 'Insetting certificate for a road freight shipment.',
        },
        product: {
            name: 'Carbon projects',
            icon: BiofuelIcon,
            href: '/carbon-projects',
        },
        description:
            "Biofuel insetting for sea, air, road and rail, with a certificate in your customer's name.",
    },
]

const Logistics = () => {
    const { mdScreen } = useDevice()

    return (
        <>
            <Box>
                <GenericHero
                    titleComponent="h1"
                    backgroundImageDesktop={'/images/heros/logistics-desktop.png'}
                    backgroundImageMobile={'/images/heros/logistics-mobile.png'}
                    desktopImageSize={{ width: 4000, height: 1584 }}
                    mobileImageSize={{ width: 1800, height: 1800 }}
                    title="The emissions data your customers ask for, in every shipment and every tender"
                    description="Lune gives freight forwarders and logistics platforms accredited CO₂ figures for every shipment, automatic reports for every customer, and lower-carbon options to offer, through API, spreadsheet or CargoWise."
                    button={{
                        href: '/request-a-demo',
                        label: 'Request a demo',
                    }}
                    secondaryButton={{
                        href: '/contact-us',
                        label: 'Contact us',
                    }}
                />
                <LogisticsLogos caption="Trusted by 100+ companies" />
            </Box>
            <RequestsShowcase
                heading="Answer your customers' emissions requests, effortlessly"
                lead="Shippers ask their logistics providers for emissions data in many forms. With Lune, you have accurate answers ready to win the business."
                items={requests}
            />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: mdScreen ? 6 : 9,
                }}
            >
                <Text variant={mdScreen ? 'h5' : 'h4'} sx={{ textAlign: 'center' }}>
                    Why logistics providers choose Lune
                </Text>
                <LearnMoreTiles
                    tiles={[
                        {
                            icon: AccreditedIcon,
                            title: 'Figures your customers can defend',
                            description:
                                'Certified GLEC and ISO 14083 compliant, with a step-by-step explanation and data quality score behind every number.',
                        },
                        {
                            icon: GranularIcon,
                            title: 'Granularity that drives reductions',
                            description:
                                'Vessel-specific modelling, AIS-tracked routes and aircraft identified by flight number reveal real differences between carriers and modes. Industry averages make every option look the same.',
                        },
                        {
                            icon: RouteIcon,
                            title: 'More from the data you have',
                            description:
                                'Emissions intelligence finds the vessel, the actual route and missing truck legs, without asking anyone for more data.',
                        },
                        {
                            icon: HubIcon,
                            title: 'One integration, end to end',
                            description:
                                'Calculations, per-customer reporting, insetting and offsetting through one API or plugin.',
                        },
                    ]}
                />
            </Box>
            <LogixboardCaseStudyBox />
            <PlugInAndGo
                title="Go live within days"
                body="Forwarders and carriers use Lune directly. Logistics software platforms embed it for their users."
                items={[
                    {
                        backgroundImageUrl: '/illustrations/plug-in-and-go/api.png',
                        title: 'REST API',
                        description: 'Multi-leg shipments in one API request, integrate in days.',
                    },
                    {
                        backgroundImageUrl: '/illustrations/plug-in-and-go/spreadsheet.png',
                        title: 'Simple spreadsheet upload',
                        description:
                            'Get started immediately and upload your logistics data via spreadsheet.',
                    },
                    {
                        backgroundImageUrl: '/illustrations/plug-in-and-go/cargowise.png',
                        title: 'CargoWise plugin',
                        description: "Activate Lune's CargoWise plugin. No code required.",
                    },
                ]}
            />
            <DownloadGuide purpleVersion={true} hubspotFormId={HUBSPOT_FORM_IDS.purpleForm} />
            <Box>
                <Text variant={'h6'} sx={{ mb: 5 }}>
                    FAQs
                </Text>
                <AccordionTile
                    title="What data do you need to calculate shipment emissions?"
                    description="At minimum, load, mode and distance (or origin and destination). Lune's emissions intelligence enriches high-level inputs, for example by identifying the vessel from carrier and dates."
                />
                <AccordionTile
                    title="Can we calculate emissions for tenders?"
                    description="Yes. Upload the lanes in bulk by spreadsheet or API. For sea freight, carrier schedules give the route before the voyage."
                />
                <AccordionTile
                    title="How long does it take to get started?"
                    description="You can go live within days, via REST API, spreadsheet upload or the CargoWise plugin."
                />
                <AccordionTile
                    title="Do you work with logistics software platforms?"
                    description="Yes. Platforms such as Logixboard embed Lune's calculations and reporting for the freight forwarders who use them."
                />
                <AccordionTile
                    title="Can our customers see their own emissions?"
                    description="Yes. Lune generates an analytics page for each of your customers."
                />
                <AccordionTile
                    title="Can we offer insetting and offsetting to our customers?"
                    description="Yes. Through Lune's API or dashboard, with certificates and project impact pages in your customer's name."
                />
            </Box>
            <RequestDemo text="Give every customer the emissions data they ask for." />
        </>
    )
}

export default Logistics
